import { gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { UserProfileFragment } from '../fragments/UserProfile';
import { OptimizedImageFragment } from '../fragments/artikler/ArtikkelSEOFragment';
import { buildSiteUrl } from '../lib/polylang';
import { createSchema, createBreadcrumbSchema } from '../lib/schema';
import {
  Header,
  Footer,
  Main,
  Container,
  NavigationMenu,
  SEO,
  SchemaMarkup,
} from '../components';
import ProjectsHero from '../components/ProjectsPage/ProjectsHero/ProjectsHero';
import ProjectsGrid from '../components/ProjectsPage/ProjectsGrid/ProjectsGrid';
import React, { useEffect, useState } from 'react';
import TopHero from '../components/skeletonloader/TopHero';
import Image from 'next/image';
import classNames from 'classnames/bind';
import styles from '../components/Customers/Customers.module.scss';

let cx = classNames.bind(styles);

export default function Oppdrag(props) {
  const [mounted, setMounted] = useState(false);
  
  // Use data from getStaticProps
  const { data, loading = false, error = null } = { data: props.data || {} };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (error) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const viewer = data?.viewer;

  // Find the hero section from the flexible content
  const heroSection = data?.page?.oppdrag?.oppdragside?.find(
    section => section.fieldGroupName === 'OppdragOppdragsideHeroLayout'
  );

  const isLoading = loading || !mounted;
  
  // Get customer logos from hero section
  const customerLogos = heroSection?.logo?.nodes?.map(logo => ({
    sourceUrl: logo.sourceUrl,
    altText: logo.altText
  })) || [];

  // Get the page title and description from the hero section
  const pageTitle = heroSection?.mainHeaderText || 'Prosjekter';
  const pageDescription = heroSection?.subText || siteDescription;
  const seoDescription = data?.page?.seo?.description || pageDescription || 'Finn bedrifter lik deg selv, som fikk en bedre digital hverdag og resultat etter samarbeid med Mediabooster. Se våre siste prosjekter nedenfor.';

  return (
    <>
      <SEO 
        locale="nb_NO"
        hreflangAlternates={[
          { hrefLang: 'no', href: buildSiteUrl('/prosjekter') },
          { hrefLang: 'en', href: buildSiteUrl('/en/projects') },
          { hrefLang: 'x-default', href: buildSiteUrl('/prosjekter') },
        ]}
        seo={{
          title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Prosjekter - Mediabooster',
          description: seoDescription,
          canonicalUrl: buildSiteUrl('/prosjekter'),
          openGraph: {
            title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Prosjekter - Mediabooster',
            description: seoDescription,
            url: buildSiteUrl('/prosjekter'),
            siteName: siteTitle || 'Mediabooster',
            type: 'website',
            image: {
              url: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png',
              secureUrl: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png',
              width: 1200,
              height: 630,
              alt: 'Mediabooster - Digital markedsføring og webdesign'
            }
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Prosjekter - Mediabooster',
            description: seoDescription,
            image: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png'
          }
        }}
      />
      <SchemaMarkup
        schema={createSchema([
          createBreadcrumbSchema([
            { label: 'Hjem', path: '/' },
            { label: 'Prosjekter', path: '/prosjekter' },
          ]),
        ])}
      />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        viewer={viewer}
        sharedData={props.sharedData}
      />
      <Main>
        {isLoading ? (
          <TopHero />
        ) : (
          <>
            {heroSection && <ProjectsHero data={heroSection} />}
            
            {/* Customer logos carousel */}
            {customerLogos.length > 0 && (
              <section className={cx('customers')}>
                <div className={cx('container')}>
                  <div className={cx('carouselWrapper')}>
                    <div className={cx('logoSlider', 'secondRow')}>
                      <div className={cx('logoTrack', 'scrollLeft')}>
                        {[...customerLogos, ...customerLogos, ...customerLogos].map((logo, index) => (
                          <div key={`customer-${index}`} className={cx('customerLogo')}>
                            <Image
                              src={logo.sourceUrl}
                              alt={logo.altText || 'Customer logo'}
                              width={160}
                              height={80}
                              className={cx('logoImage')}
                              style={{
                                objectFit: 'contain',
                                objectPosition: 'center'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            <ProjectsGrid 
              categories={data?.prosjektKategorier?.nodes} 
              projects={data?.prosjekter?.nodes}
            />
          </>
        )}
      </Main>
      <Footer 
        title={siteTitle} 
        menuItems={primaryMenu}
        settings={data}
        sharedData={props.sharedData}
      />
    </>
  );
}

Oppdrag.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${UserProfileFragment}
  ${OptimizedImageFragment}
  query GetOppdragPage(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    ...UserProfileFragment
    page(id: "18", idType: DATABASE_ID) {
      oppdrag {
        oppdragside {
          ... on OppdragOppdragsideHeroLayout {
            fieldGroupName
            mainHeaderText
            smallTopHeader
            subText
            button {
              buttonLink {
                target
                title
                url
              }
              buttonText
            }
            logo(first: 100) {
              nodes {
                id
                sourceUrl
                altText
                title
              }
            }
          }
        }
      }
      seo {
        title
        description
        fullHead
        canonicalUrl
        robots
        openGraph {
          title
          description
          image {
            secureUrl
            url
          }
        }
      }
    }
    prosjektKategorier {
      nodes {
        id
        name
        slug
        count
      }
    }
    prosjekter(first: 100, where: { language: NB }) {
      nodes {
        id
        title
        slug
        uri
        featuredImage {
          node {
            ...OptimizedImageFragment
          }
        }
        prosjektKategorier {
          nodes {
            id
            slug
          }
        }
      }
    }
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

Oppdrag.variables = () => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
  };
};

// Add getStaticProps for server-side data fetching with shared data
export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: Oppdrag.query,
      variables: Oppdrag.variables(),
      fetchPolicy: 'no-cache'
    });
    
    // Fetch shared data using exact same queries as components
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        query GetSharedProsjekterData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # MediaBooster settings - includes both Footer and ContactCTA data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              logo {
                node {
                  mediaItemUrl
                  altText
                }
              }
              logoIcon {
                node {
                  mediaItemUrl
                  altText
                }
              }
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
            }
          }
          
          # Frontpage tjenesteboks data used by Header
          page(id: "14", idType: DATABASE_ID) {
            frontPage {
              tjenesteboks {
                tjenestenavn
                underoverskrift
                link
              }
            }
          }

          employees: ansatte(first: 100, where: { language: NB }) {
            nodes {
              id
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: pageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching prosjekter data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}



